import { CSSProperties, useEffect, useRef, useState } from "react";
import Supercluster from "supercluster";
import { useSelectedProjectSelector } from "../../store/projects/selectors";
import ReactDOMServer from "react-dom/server";
import _ from "lodash";
import { cachedRequest } from "../../utils/idb";
import { dataStyle, mapStyles } from "./GoogleMapStyle";

export type CiidMapProps = {
  readonly mapContainerStyle: CSSProperties;
};

export function CiidMap({ mapContainerStyle }: CiidMapProps) {
  const [geoJsonObject, setGeoJsonObject] = useState<object | null>(null);
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const carbonAllMarkers = useRef<any[]>([]);
  const recommendationsAllMarkers = useRef<any[]>([]);
  const superclusterIndex = useRef<Supercluster>();
  const markers = useRef<any[]>([]);
  const mapRef = useRef<any>(null);
  const ref = useRef<HTMLDivElement>(null);

  const selectedType = useSelectedProjectSelector();

  // Check if Google Maps API is loaded
  useEffect(() => {
    const checkGoogleMaps = () => {
      if (typeof window !== "undefined" && window.google && window.google.maps) {
        setIsGoogleMapsLoaded(true);
        return true;
      }
      return false;
    };

    // Check immediately
    if (checkGoogleMaps()) {
      return;
    }

    // If not loaded, check periodically
    const interval = setInterval(() => {
      if (checkGoogleMaps()) {
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (mapRef.current && geoJsonObject) {
      // Clear existing features
      mapRef.current.data.forEach((feature: google.maps.Data.Feature) => {
        mapRef.current?.data.remove(feature);
      });

      // Add new features
      mapRef.current.data.setStyle(dataStyle);
      mapRef.current.data.addGeoJson(geoJsonObject);
    }
  }, [geoJsonObject, mapRef]);

  // Initialize Supercluster
  useEffect(() => {
    superclusterIndex.current = new Supercluster({
      radius: 200,
      maxZoom: 16,
      minZoom: 1,
      map: (props) => ({
        sum: props.sum,
        ...props,
      }),
      reduce: (accumulated, props) => {
        accumulated.sum = (accumulated.sum || 0) + (props.sum || 1);
      },
    });
    return () => {
      if (mapRef.current && window.google && window.google.maps) {
        try {
          // Clear markers
          markers.current.forEach((marker) => {
            if (marker && marker.setMap) {
              marker.setMap(null);
            }
          });
          markers.current = [];

          // Clear data layer features
          if (mapRef.current.data) {
            mapRef.current.data.forEach((feature: google.maps.Data.Feature) => {
              mapRef.current?.data.remove(feature);
            });
          }
        } catch (error) {
          console.error("Error cleaning up markers:", error);
        }
      }
    };
  }, []);

  // Initialize Google Maps
  useEffect(() => {
    if (!isGoogleMapsLoaded) {
      return;
    }

    if (!mapRef.current && ref.current && window.google && window.google.maps) {
      const map = new window.google.maps.Map(ref.current, {
        center: { lat: 20.5937, lng: 78.9629 },
        zoom: 4,
        streetViewControl: false,
        mapTypeControl: false,
        styles: mapStyles,
      });
      mapRef.current = map;
    }
    return () => {
      if (mapRef.current && window.google && window.google.maps) {
        // Clear all event listeners
        window.google.maps.event.clearInstanceListeners(mapRef.current);
        mapRef.current = null;
      }
    };
  }, [isGoogleMapsLoaded]);

  const createGeoJSONPoint = (position: { lat: number; lng: number }, properties: any) => ({
    type: "Feature",
    geometry: {
      type: "Point",
      coordinates: [position.lng, position.lat],
    },
    properties: {
      ...properties,
      sum: 1,
    },
  });

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const [, carbonData, recommendationData] = await Promise.all([
          cachedRequest("/admin_levels", { method: "GET" }, "admin_levels_cache_key", 3000000).then(
            (data) => setGeoJsonObject(data),
          ),
          cachedRequest(
            "/projects/carbon/all/markers",
            { method: "POST", data: { limit: 50000 } },
            "projects_cache_key1",
            3000000,
          ),
          cachedRequest(
            "/v2/recommendations/all/markers",
            { method: "GET" },
            "projects_recommendations_cache_key1",
            3000000,
          ),
        ]);

        carbonAllMarkers.current = carbonData.map((item: any) =>
          createGeoJSONPoint(
            { lat: item.lat, lng: item.lng },
            {
              name: item.name,
              status: item.status,
              id: item.id,
              type: "carbon",
            },
          ),
        );

        recommendationsAllMarkers.current = recommendationData.map((item: any) =>
          createGeoJSONPoint(
            { lat: item.lat, lng: item.lng },
            {
              name: item.name,
              status: item.status,
              id: item.id,
              type: "recommendation",
            },
          ),
        );

        updateClusters();
      } catch (err) {
        console.error("Failed to fetch project data:", err);
      }
    };

    fetchProjects();

    return () => {
      if (window.google && window.google.maps) {
        try {
          markers.current.forEach((marker) => {
            if (marker && marker.setMap) {
              marker.setMap(null);
            }
          });
        } catch (error) {
          console.error("Error cleaning up markers on unmount:", error);
        }
      }
      markers.current = [];
      carbonAllMarkers.current = [];
      recommendationsAllMarkers.current = [];
    };
  }, []);

  const updateClusters = () => {
    // Check if Google Maps API is loaded
    if (!isGoogleMapsLoaded || !window.google || !window.google.maps) {
      return;
    }

    const map = mapRef.current;
    if (!map || !superclusterIndex.current) return;

    // Clear existing markers safely
    try {
      markers.current.forEach((marker) => {
        if (marker && marker.setMap) {
          marker.setMap(null);
        }
      });
    } catch (error) {
      console.error("Error clearing markers:", error);
    }
    markers.current = [];

    const bounds = map.getBounds();
    const zoom = map.getZoom();

    if (!bounds || zoom === undefined) return;

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    const points =
      selectedType === "recommendation"
        ? recommendationsAllMarkers.current
        : carbonAllMarkers.current;

    superclusterIndex.current.load(points);

    const clusters = superclusterIndex.current.getClusters(
      [sw.lng(), sw.lat(), ne.lng(), ne.lat()],
      Math.floor(zoom),
    );
    function getClusterColor(count: number) {
      const maxCount = Math.max(...clusters.map((c) => c.properties?.point_count || 0));
      const ratio = 1 - count / maxCount; // Inverted ratio
      const blue = Math.round(180 + 155 * ratio); // Range from light blue (50) to dark blue (255)
      return `rgb(0, 0, ${blue})`;
    }
    clusters.forEach((cluster) => {
      const [lng, lat] = cluster.geometry.coordinates;
      const count = cluster.properties?.point_count || 0;

      if (cluster.properties.cluster) {
        const marker = new window.google.maps.Marker({
          position: { lat, lng },
          map,
          label: {
            text: count.toString(),
            color: "white",
          },
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: Math.max(20, Math.min(count * 3, 25)),
            fillColor: getClusterColor(count), // Add color function
            fillOpacity: 1,
            strokeWeight: 1,
            strokeColor: "#FFFFFF",
          },
        });

        marker.addListener("click", () => {
          const expansionZoom = superclusterIndex.current?.getClusterExpansionZoom(
            cluster.properties.cluster_id,
          );
          if (expansionZoom) {
            map.setZoom(expansionZoom);
            map.panTo({ lat, lng });
          }
        });

        markers.current.push(marker);
      } else {
        const marker = new window.google.maps.Marker({
          position: { lat, lng },
          map,
          icon: {
            url: "http://maps.google.com/mapfiles/ms/icons/blue-dot.png",
          },
        });

        const href =
          selectedType === "recommendation"
            ? `/recommendation/mangrove/${cluster.properties.id}`
            : `/project/${cluster.properties.id}`;

        marker.addListener("click", () => {
          const props = cluster.properties;
          const infoContent = ReactDOMServer.renderToString(
            <div className="text-left">
              <h3 className="mb-2">Name: {props.name}</h3>
              <p className="mb-2">Status: {props.status}</p>
              <a href={href} className="text-blue-500 underline hover:text-blue-700">
                More details
              </a>
            </div>,
          );

          const info = new window.google.maps.InfoWindow({
            content: infoContent,
            ariaLabel: props.name,
          });
          info.open({
            anchor: marker,
            map,
          });
        });

        markers.current.push(marker);
      }
    });
  };

  useEffect(() => {
    if (mapRef.current && isGoogleMapsLoaded && window.google && window.google.maps) {
      const boundsListener = mapRef.current.addListener(
        "bounds_changed",
        _.debounce(updateClusters, 300),
      );
      return () => {
        if (window.google && window.google.maps) {
          window.google.maps.event.removeListener(boundsListener);
        }
      };
    }
  }, [selectedType, isGoogleMapsLoaded]);

  useEffect(() => {
    updateClusters();
  }, [selectedType]);

  return (
    <div
      style={{
        ...mapContainerStyle,
        willChange: "transform",
        transform: "translateZ(0)",
      }}
      ref={ref}
      id="map"
    >
      {!isGoogleMapsLoaded && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: "100%",
            position: "absolute",
            top: 0,
            left: 0,
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            zIndex: 1000,
          }}
        >
          <div>Loading Google Maps...</div>
        </div>
      )}
    </div>
  );
}
